import { Timestamp } from "./timestamp";

/**
 * Stable product identifiers. Extend this union as new products ship.
 *
 * Precedence rules (document once, apply everywhere):
 *  1. If `productEntitlements` has an entry for a given ProductKey, that entry
 *     is authoritative for gating that product (other keys may still use
 *     legacy fallbacks). Absence of a doc for that key means no override—use
 *     legacy fallbacks. A doc with `state: "revoked"` is an explicit deny and
 *     does not fall back.
 *  2. If that key is absent, fall back to legacy signals (`subscription`,
 *     `leadsEnabled`, `accountType`) for that key only. See
 *     `hasProductAccessWithFallback`.
 *  3. `source` on each entitlement records *who* wrote it:
 *     - "stripe"  — written by webhook reconciliation; only Stripe events may
 *                   mutate rows where source === "stripe".
 *     - "manual"  — written by an admin; Stripe events must not overwrite
 *                   manual rows unless a documented conflict policy applies.
 *     - "system"  — written by automated migration / backfill.
 *  4. Two independent time windows can close access, and both must be open:
 *     the account-level `BusinessData.accessValidUntil` ceiling, and the
 *     per-product `validFrom` / `validUntil` on the entitlement itself. The
 *     account ceiling applies to every product regardless of `source`; it is a
 *     read-time deny and never mutates a Stripe-owned row.
 */
export type ProductKey = "recruit" | "leads" | "intelligence";

export type EntitlementState =
  | "inactive"
  | "trialing"
  | "active"
  | "grace"
  | "revoked";

export type EntitlementSource = "stripe" | "manual" | "system";

export interface ProductEntitlement {
  state: EntitlementState;
  source: EntitlementSource;
  /** When source is "stripe", the Stripe subscription that drives this entitlement. */
  stripeSubscriptionId?: string;
  /** When source is "stripe", the price / subscription-item driving this product. */
  stripePriceId?: string;
  /** When source is "stripe", the period end from the subscription. */
  stripeCurrentPeriodEnd?: Timestamp;
  /** Start of the entitlement window (manual grants). */
  validFrom?: Timestamp;
  /** End of the entitlement window (manual grants, trials). */
  validUntil?: Timestamp;
  updatedAt: Timestamp;
}

export type ProductEntitlements = Partial<Record<ProductKey, ProductEntitlement>>;

// ---------------------------------------------------------------------------
// Pure helpers — safe to use in any environment (browser, server, edge).
// ---------------------------------------------------------------------------

const ACTIVE_STATES: ReadonlySet<EntitlementState> = new Set([
  "active",
  "trialing",
  "grace",
]);

/** Entitlement states that grant product access (for Firestore `in` queries). */
export const ACCESSIBLE_ENTITLEMENT_STATES: readonly EntitlementState[] = [
  "active",
  "trialing",
  "grace",
];

/**
 * Anything that can carry an instant across our storage and transport layers:
 * a Firestore `Timestamp` (client or admin — both expose `seconds`), the plain
 * `{ seconds, nanoseconds }` map we write for manual grants, a `Date`, an epoch
 * millisecond number, or an ISO string (how the admin dashboard serializes).
 */
export type TimestampLike = Timestamp | Date | string | number;

/**
 * Normalize a {@link TimestampLike} to epoch milliseconds.
 * Returns `null` for nullish or unparseable input, so an unreadable value
 * behaves like "no limit set" rather than silently denying access.
 */
export function toEpochMillis(
  value: TimestampLike | null | undefined,
): number | null {
  if (value == null) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isNaN(ms) ? null : ms;
  }
  if (typeof value === "string") {
    const ms = Date.parse(value);
    return Number.isNaN(ms) ? null : ms;
  }
  // Firestore admin `Timestamp` deserialized from JSON exposes `_seconds`.
  const raw = value as Partial<Timestamp> & {
    _seconds?: number;
    _nanoseconds?: number;
    toMillis?: () => number;
  };
  if (typeof raw.toMillis === "function") {
    const ms = raw.toMillis();
    return Number.isFinite(ms) ? ms : null;
  }
  const seconds = typeof raw.seconds === "number" ? raw.seconds : raw._seconds;
  if (typeof seconds !== "number" || !Number.isFinite(seconds)) return null;
  const nanos =
    typeof raw.nanoseconds === "number" ? raw.nanoseconds : raw._nanoseconds;
  return seconds * 1000 + (typeof nanos === "number" ? nanos / 1e6 : 0);
}

/** The account-level fields consulted by the access helpers. */
export interface AccountAccessWindow {
  /** Hard expiry for the whole account; unset means open-ended. */
  accessValidUntil?: TimestampLike | null;
}

/** Everything the access helpers read off a business document. */
export interface ProductAccessBusiness extends AccountAccessWindow {
  productEntitlements?: ProductEntitlements;
  interestedProducts?: string[];
  leadsEnabled?: boolean;
  subscription?: { status: string } | null;
  accountType?: string;
}

/**
 * True when the account-level time limit has passed.
 *
 * `accessValidUntil` is the last instant of access (inclusive), so the account
 * is expired strictly *after* it. Businesses with no limit set are never
 * expired. `now` is passed in rather than read from the clock so this stays
 * pure and testable.
 */
export function isAccountExpired(
  business: AccountAccessWindow | null | undefined,
  now: number,
): boolean {
  const until = toEpochMillis(business?.accessValidUntil);
  return until != null && now > until;
}

/**
 * True when `now` falls inside the entitlement's own `validFrom`/`validUntil`
 * window. Absent bounds are treated as open-ended on that side.
 */
export function isEntitlementWindowOpen(
  entitlement: Pick<ProductEntitlement, "validFrom" | "validUntil">,
  now: number,
): boolean {
  const from = toEpochMillis(entitlement.validFrom);
  if (from != null && now < from) return false;
  const until = toEpochMillis(entitlement.validUntil);
  if (until != null && now > until) return false;
  return true;
}

/**
 * Check whether a specific product is accessible from entitlements alone
 * (no legacy fallback), honouring both the account-level ceiling and the
 * entitlement's own validity window.
 */
export function hasProductAccess(
  business: ProductAccessBusiness | null | undefined,
  key: ProductKey,
  now: number,
): boolean {
  if (isAccountExpired(business, now)) return false;
  const ent = business?.productEntitlements?.[key];
  if (ent == null) return false;
  return ACTIVE_STATES.has(ent.state) && isEntitlementWindowOpen(ent, now);
}

/**
 * Same check but with fallback to legacy fields for businesses that have not
 * been backfilled yet. Use this during the migration window.
 *
 * The account-level ceiling wins over every fallback: an expired account has
 * no product access even if the legacy signals say otherwise.
 */
export function hasProductAccessWithFallback(
  business: ProductAccessBusiness,
  key: ProductKey,
  now: number,
): boolean {
  if (isAccountExpired(business, now)) return false;

  const ent = business.productEntitlements?.[key];
  if (ent != null) {
    return ACTIVE_STATES.has(ent.state) && isEntitlementWindowOpen(ent, now);
  }

  switch (key) {
    case "recruit":
      if (
        business.subscription?.status === "active" ||
        business.subscription?.status === "trialing"
      ) {
        return true;
      }
      return ["pro", "premium", "unlimited"].includes(
        business.accountType ?? "",
      );

    case "leads":
      return business.leadsEnabled === true;

    case "intelligence":
      return false;

    default:
      return false;
  }
}

// ---------------------------------------------------------------------------
// Product first-run onboarding (in-product setup after sales-led activation)
// ---------------------------------------------------------------------------

export interface ProductOnboardingEntry {
  completedAt: Timestamp;
  /** True when the user dismissed the wizard instead of finishing it. */
  skipped?: boolean;
}

/** Per-product first-run wizard state stored on the business document. */
export type ProductOnboardingState = Partial<
  Record<ProductKey, ProductOnboardingEntry>
>;

export type BillingEra =
  | "none"
  | "legacy_fields"
  | "stripe_summary"
  | "entitlements";

/** Detect which billing era a business document belongs to. */
export function detectBillingEra(business: {
  productEntitlements?: ProductEntitlements;
  subscription?: { status: string } | null;
  stripe_customer_id?: string;
  sub_still_active?: string;
  paid?: boolean;
}): BillingEra {
  if (
    business.productEntitlements &&
    Object.keys(business.productEntitlements).length > 0
  ) {
    return "entitlements";
  }
  if (business.subscription) {
    return "stripe_summary";
  }
  if (
    business.stripe_customer_id ||
    business.sub_still_active ||
    business.paid
  ) {
    return "legacy_fields";
  }
  return "none";
}
